import { useQuery } from '@tanstack/react-query';
import { Card, Descriptions, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { StoresSummaryDayGrid } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { useStickyTable } from '../../context/StickyTableContext';
import { formatKopeks } from '../../lib/money';

type SummaryDay = StoresSummaryDayGrid['days'][number];
type SummaryStore = StoresSummaryDayGrid['stores'][number];

/** Right-aligned money, «—» for zero, tabular figures for column alignment. */
function money(n: number): JSX.Element {
  return n ? (
    <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {formatKopeks(n)}
    </span>
  ) : (
    <Typography.Text type="secondary">—</Typography.Text>
  );
}

/** «2,5%» / «2,5% + 1%» — the store's fee (+НДС) rates behind the deduction. */
function bpsLabel(s: SummaryStore): string {
  const pct = (bps: number): string => `${(bps / 100).toLocaleString('ru-RU')}%`;
  const parts: string[] = [];
  if (s.terminalFeeBps > 0) parts.push(pct(s.terminalFeeBps));
  if (s.taxBps > 0) parts.push(`+ ${pct(s.taxBps)}`);
  return parts.join(' ');
}

// Store has no accent color (unlike Sklad) — alternate groups get a light
// zebra tint so the 3-column blocks stay visually separated.
const GROUP_HEADER_TINT = 'rgba(0, 0, 0, 0.04)';
const GROUP_CELL_TINT = 'rgba(0, 0, 0, 0.02)';

const FIELDS = ['cash', 'terminal', 'expenses'] as const;
const FIELD_LABEL_KEY: Record<(typeof FIELDS)[number], string> = {
  cash: 'daygrid.colCash',
  terminal: 'daygrid.colTerminal',
  expenses: 'daygrid.colExpenses',
};

/**
 * «Сводка» day grid: one row per calendar day, one column-group per store
 * (Наличные / Терминал / Расходы). The summary block shows per-store month
 * totals, the terminal commission (+НДС) subtracted, the net («Чистыми») per
 * store and the grand net. Read-only.
 */
export function SummaryDayGridTable({ month }: { month: string }): JSX.Element {
  const { t } = useTranslation();
  const sticky = useStickyTable();

  const query = useQuery({
    queryKey: queryKeys.summaryDayGrid(month),
    queryFn: () => reportsApi.storesSummaryDayGrid(month),
  });

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;
  const grid = query.data;
  const { totals, grand } = grid;
  const today = dayjs().format('YYYY-MM-DD');
  const groupTint = (gi: number): { style: { background: string } } | Record<string, never> =>
    gi % 2 ? { style: { background: GROUP_CELL_TINT } } : {};

  const columns: ColumnsType<SummaryDay> = [
    {
      title: t('common.date'),
      key: 'date',
      fixed: 'left',
      width: 62,
      align: 'left',
      render: (_v: unknown, row: SummaryDay) => {
        const d = dayjs(row.date);
        const weekend = d.day() === 0 || d.day() === 6;
        return (
          <span
            style={{ fontVariantNumeric: 'tabular-nums', color: weekend ? '#cf1322' : undefined }}
          >
            {d.format('DD.MM')}
          </span>
        );
      },
    },
    ...grid.stores.map((s, gi) => ({
      title: s.name,
      key: `store-${s.id}`,
      onHeaderCell: () => (gi % 2 ? { style: { background: GROUP_HEADER_TINT } } : {}),
      children: FIELDS.map((f) => ({
        title: t(FIELD_LABEL_KEY[f]),
        key: `${s.id}-${f}`,
        align: 'right' as const,
        width: 92,
        onHeaderCell: () => (gi % 2 ? { style: { background: GROUP_HEADER_TINT } } : {}),
        onCell: () => groupTint(gi),
        render: (_v: unknown, row: SummaryDay) => money(row.byStore[s.id]?.[f] ?? 0),
      })),
    })),
  ];

  // Summary rows use LEAF indices: 0 = date, then 3 per store. `colSpan`ned
  // cells still advance `index` by the number of leaves they cover.
  const summary = (): JSX.Element => (
    <Table.Summary>
      <Table.Summary.Row style={{ fontWeight: 600, background: 'rgba(0,0,0,0.02)' }}>
        <Table.Summary.Cell index={0}>
          <strong>{t('daygrid.total')}</strong>
        </Table.Summary.Cell>
        {grid.stores.flatMap((s, si) =>
          FIELDS.map((f, fi) => (
            <Table.Summary.Cell key={`${s.id}-${f}`} index={1 + si * 3 + fi} align="right">
              {money(totals[s.id]?.[f] ?? 0)}
            </Table.Summary.Cell>
          )),
        )}
      </Table.Summary.Row>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>{t('daygrid.colTerminalFee')}</Table.Summary.Cell>
        {grid.stores.map((s, si) => {
          const st = totals[s.id];
          const charge = (st?.fee ?? 0) + (st?.tax ?? 0);
          return (
            <Table.Summary.Cell key={s.id} index={1 + si * 3} colSpan={3} align="right">
              {s.terminalFeeBps === 0 && s.taxBps === 0 ? (
                <Typography.Text type="secondary">—</Typography.Text>
              ) : (
                <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  −{formatKopeks(charge)}{' '}
                  <Typography.Text type="secondary">({bpsLabel(s)})</Typography.Text>
                </span>
              )}
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
      <Table.Summary.Row style={{ fontWeight: 600, background: 'rgba(0,0,0,0.02)' }}>
        <Table.Summary.Cell index={0}>
          <strong>{t('summary.net')}</strong>
        </Table.Summary.Cell>
        {grid.stores.map((s, si) => (
          <Table.Summary.Cell key={s.id} index={1 + si * 3} colSpan={3} align="right">
            <strong style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {formatKopeks(totals[s.id]?.net ?? 0)}
            </strong>
          </Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
      {grid.stores.length > 1 ? (
        <Table.Summary.Row style={{ fontWeight: 600 }}>
          <Table.Summary.Cell index={0}>
            <strong>{t('summary.grand')}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} colSpan={grid.stores.length * 3} align="right">
            <strong style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {formatKopeks(grand.net)}
            </strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      ) : null}
    </Table.Summary>
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Table<SummaryDay>
        dataSource={grid.days}
        columns={columns}
        rowKey="date"
        size="small"
        bordered
        pagination={false}
        sticky={sticky}
        scroll={{ x: 'max-content' }}
        rowClassName={(row) => (row.date === today ? 'jadval-today' : '')}
        summary={summary}
      />
      {/* Grand breakdown — readable on mobile where the wide table scrolls. */}
      <Card size="small" title={t('summary.grand')}>
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2, md: 3 }}
          items={[
            { key: 'cash', label: t('daygrid.colCash'), children: formatKopeks(grand.cash) },
            {
              key: 'terminal',
              label: t('daygrid.colTerminal'),
              children: formatKopeks(grand.terminal),
            },
            {
              key: 'expenses',
              label: t('daygrid.colExpenses'),
              children: formatKopeks(grand.expenses),
            },
            {
              key: 'fee',
              label: t('daygrid.colTerminalFee'),
              children: `−${formatKopeks(grand.fee)}`,
            },
            ...(grand.tax > 0
              ? [
                  {
                    key: 'tax',
                    label: t('daygrid.colTax'),
                    children: `−${formatKopeks(grand.tax)}`,
                  },
                ]
              : []),
            {
              key: 'net',
              label: t('summary.net'),
              children: <strong>{formatKopeks(grand.net)}</strong>,
            },
          ]}
        />
      </Card>
    </div>
  );
}
